import { useTranslation } from 'react-i18next';
import useReveal from '../hooks/useReveal';
import StatsStrip from '../components/StatsStrip';
import PageMeta from '../components/PageMeta';
import { COMPANY } from '../data/content';

export default function About() {
  const { t } = useTranslation();
  useReveal();

  const principles = t('principles.items', { returnObjects: true }) || [];
  const locales = t('about_page.locales', { returnObjects: true }) || [];

  return (
    <div data-testid="about-page">
      <PageMeta pageKey="about" />
      <section className="px-6 md:px-12 pt-16 md:pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-8">{t('about_page.eyebrow')}</p>
          <h1 className="reveal font-serif text-5xl md:text-7xl lg:text-8xl text-[#0B1F3F] leading-[1.02] tracking-tight max-w-5xl">
            {t('about_page.title_pre')} <span className="italic">{t('about_page.title_italic')}</span>{t('about_page.title_post')}
          </h1>
          <p className="mt-10 max-w-2xl text-[#6B5B4A] text-base leading-relaxed">
            {t('about_page.intro')}
          </p>
        </div>
      </section>

      <StatsStrip />

      <section className="px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-6">{t('about_page.founding_idea_eyebrow')}</p>
            <h2 className="reveal font-serif text-3xl md:text-4xl text-[#0B1F3F] leading-tight">
              &ldquo;{t('about_page.founding_idea_quote')}&rdquo;
            </h2>
            <p className="mt-8 text-[#6B5B4A] text-sm tracking-[0.15em] uppercase">
              {t('about_page.founding_idea_attribution')}
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6 text-[#6B5B4A] text-base leading-relaxed">
            <p>{t('about_page.body_p1')}</p>
            <p>{t('about_page.body_p2')}</p>
            <p>{t('about_page.body_p3')}</p>
          </div>
        </div>
      </section>

      <section className="border-y border-[#0B1F3F]/10 bg-[#EBE3D0] py-24 md:py-32 px-6 md:px-12 relative overflow-hidden">
        <img src={COMPANY.markWhite} alt="" aria-hidden="true" className="absolute -right-[6vw] top-1/2 -translate-y-1/2 h-[90vh] w-auto opacity-[0.06] pointer-events-none hidden md:block" />
        <div className="relative max-w-3xl mx-auto text-center">
          <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-8">{t('about_page.emblem_eyebrow')}</p>
          <h2 className="reveal font-serif italic text-3xl md:text-5xl text-[#0B1F3F] leading-tight mb-8">
            {t('about_page.emblem_title')}
          </h2>
          <p className="text-[#6B5B4A] leading-relaxed">
            {t('about_page.emblem_body')}
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-6">{t('principles.eyebrow')}</p>
          <h2 className="reveal font-serif text-4xl md:text-5xl text-[#0B1F3F] mb-16 leading-tight max-w-3xl">
            {t('about_page.principles_title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[1px] bg-[#0B1F3F]/8 border border-[#0B1F3F]/10">
            {principles.map((p) => (
              <div key={p.number} className="bg-[#EBE3D0] p-10 md:p-14">
                <p className="font-serif text-6xl text-[#B58F45] mb-6 leading-none">{p.number}</p>
                <h3 className="font-serif text-2xl md:text-3xl text-[#0B1F3F] mb-4">{p.title}</h3>
                <p className="text-[#6B5B4A] leading-relaxed text-[15px]">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#0B1F3F]/10 bg-[#F5EFE1] px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-6">{t('about_page.where_eyebrow')}</p>
            <h2 className="reveal font-serif text-4xl md:text-5xl text-[#0B1F3F] leading-tight">
              {t('about_page.where_title')}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 grid grid-cols-1 sm:grid-cols-2 gap-10">
            {locales.map((l) => (
              <div key={l.title} className="border-t border-[#0B1F3F]/10 pt-6">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#6B5B4A] mb-3">{l.label}</p>
                <h3 className="font-serif text-2xl text-[#0B1F3F] mb-4">{l.title}</h3>
                <p className="text-[14px] text-[#6B5B4A] leading-relaxed">{l.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
